import { useState, useMemo } from "react";
import { PEPTIDES as RAW_PEPTIDES } from "./Bible";
import { buildIndex, searchIndex } from "./vaultSearch";

// Guard: if the Bible export fails or is malformed, fall back to an empty array
// instead of throwing at module load (which blanks the whole page).
const PEPTIDES = Array.isArray(RAW_PEPTIDES) ? RAW_PEPTIDES : [];

const ROUTES = [
  {key:"food",      label:"🥦 Food / Diet",  color:"#475569"},
  {key:"oral",      label:"💊 Oral Std",      color:"#3b82f6"},
  {key:"liposomal", label:"🧬 Liposomal",     color:"#22d3ee"},
  {key:"subcut",    label:"💉 SC / IV",        color:"#22c55e"},
];

const SPAWN_CATS = ["Spawn Nutra"];
const isSpawn = (name) => {
  const n = String(name || "").toLowerCase();
  return n.includes("biopro") || n.includes("immortal") || n.includes("spawn nutra");
};

// Live count of compounds carrying bioavailability data
const BIOAVAIL_COUNT = PEPTIDES.filter(p => p && p.bioavailNum).length;

// Stable unique id per record (names repeat across categories, which made rows vanish or open in pairs).
const ROW_ID = new Map();
PEPTIDES.forEach((p, i) => { if (p) ROW_ID.set(p, `r${i}`); }); // forEach skips holes in the array
const BIO_INDEX = buildIndex(PEPTIDES.filter(p => p && p.bioavailNum));

export default function BioavailabilityApp() {
  const [catFilter, setCatFilter]   = useState("All");
  const [search,    setSearch]      = useState("");
  const [selected,  setSelected]    = useState(null);
  const [sortBy,    setSortBy]      = useState("liposomal");
  const [showOnly,  setShowOnly]    = useState("all"); // all | spawn | high

  // Build category list from Bible data
  const CATEGORIES = useMemo(() => {
    const cats = new Set(PEPTIDES.map(p => p && p.category).filter(Boolean));
    return ["All", ...Array.from(cats).sort()];
  }, []);

  // Filter + sort compounds that have bioavailNum
  const compounds = useMemo(() => {
    const hits = searchIndex(BIO_INDEX, search);
    const matchSet = hits ? new Set(hits.map(h => h.row.rec)) : null;
    return PEPTIDES
      .filter(p => p && p.bioavailNum)
      .filter(p => catFilter === "All" || p.category === catFilter)
      .filter(p => !matchSet || matchSet.has(p))
      .filter(p => {
        if (showOnly === "spawn") return isSpawn(p.name);
        if (showOnly === "high")  return ((p.bioavailNum.liposomal || 0) - (p.bioavailNum.oral || 0)) >= 30;
        return true;
      })
      .sort((a,b) => ((b.bioavailNum && b.bioavailNum[sortBy]) || 0) - ((a.bioavailNum && a.bioavailNum[sortBy]) || 0));
  }, [catFilter, search, sortBy, showOnly]);

  // Top liposomal advantage compounds
  const topAdvantage = useMemo(() => {
    return [...PEPTIDES]
      .filter(p => p && p.bioavailNum)
      .map(p => ({...p, advantage: (p.bioavailNum.liposomal || 0) - (p.bioavailNum.oral || 0)}))
      .sort((a,b) => b.advantage - a.advantage)
      .slice(0, 6);
  }, []);

  // Fallback render: never hand React a blank page if the database failed to load.
  if (!PEPTIDES.length) {
    return (
      <div style={{minHeight:"100vh", background:"#060608", color:"#dc2626", fontFamily:"'Courier New',monospace", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px", textAlign:"center", fontSize:13, letterSpacing:"0.08em"}}>
        BIOAVAILABILITY DATA UNAVAILABLE — the compound database did not load.
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh", background:"#060608", fontFamily:"'Courier New',monospace", color:"#e2e8f0"}}>

      {/* HEADER */}
      <div style={{background:"linear-gradient(180deg,#0d0000,#130404 60%,#0a0a0a)", borderBottom:"3px solid #dc2626", overflow:"hidden", position:"relative"}}>
        <div style={{display:"flex", alignItems:"stretch", maxWidth:960, margin:"0 auto", padding:"0 20px"}}>
          <div style={{flex:1, padding:"12px 16px", display:"flex", flexDirection:"column", justifyContent:"center"}}>
            <div style={{fontSize:9, color:"#dc2626", letterSpacing:"0.2em", marginBottom:3}}>Spawn Nutra Science</div>
            <div style={{fontFamily:"'Impact','Arial Black',sans-serif", fontSize:22, color:"#f5c800", letterSpacing:"0.08em", lineHeight:1, textShadow:"2px 2px 0 #7f1d1d"}}>BIOAVAILABILITY BIBLE</div>
            <div style={{fontSize:9, color:"#94a3b8", letterSpacing:"0.15em", marginTop:4}}>LIVE FROM THE BIBLE DATABASE · 4 ROUTES · LIPOSOMAL vs STANDARD</div>
            </div>
          </div>
      </div>

      <div style={{maxWidth:960, margin:"0 auto", padding:"20px"}}>

        {/* LEGEND */}
        <div style={{display:"flex", gap:16, flexWrap:"wrap", marginBottom:16, padding:"10px 16px", background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8}}>
          {ROUTES.map(r=>(
            <div key={r.key} style={{display:"flex", alignItems:"center", gap:6, fontSize:11}}>
              <div style={{width:12, height:12, borderRadius:2, background:r.color}}/>
              <span style={{color:"#94a3b8"}}>{r.label}</span>
            </div>
          ))}
          <div style={{marginLeft:"auto", fontSize:9, color:"#f5c800"}}>★ BioPro + Immortal highlighted</div>
        </div>

        {/* FILTERS */}
        <div style={{display:"flex", gap:8, marginBottom:16, flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search all ${BIOAVAIL_COUNT} compounds...`}
            style={{flex:1, minWidth:140, padding:"8px 12px", background:"#0f0f0f", border:"1px solid #2a2a2a", borderRadius:6, color:"#e2e8f0", fontSize:11, fontFamily:"inherit", outline:"none"}}/>
          <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
            style={{padding:"8px 10px", background:"#0f0f0f", border:"1px solid #2a2a2a", borderRadius:6, color:"#94a3b8", fontSize:11, fontFamily:"inherit", cursor:"pointer"}}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{padding:"8px 10px", background:"#0f0f0f", border:"1px solid #2a2a2a", borderRadius:6, color:"#94a3b8", fontSize:11, fontFamily:"inherit", cursor:"pointer"}}>
            <option value="liposomal">Sort: Liposomal</option>
            <option value="oral">Sort: Oral</option>
            <option value="subcut">Sort: SC/IV</option>
            <option value="food">Sort: Food</option>
          </select>
          <div style={{display:"flex", gap:4}}>
            {[{v:"all",l:"All"},{v:"spawn",l:"★ Spawn Nutra"},{v:"high",l:"⚡ High Δ"}].map(btn=>(
              <button key={btn.v} onClick={()=>setShowOnly(btn.v)}
                style={{padding:"8px 10px", background:showOnly===btn.v?"rgba(220,38,38,0.15)":"#0f0f0f", border:"1px solid", borderColor:showOnly===btn.v?"#dc2626":"#2a2a2a", borderRadius:6, color:showOnly===btn.v?"#dc2626":"#6b7280", fontSize:10, cursor:"pointer", fontFamily:"inherit"}}>
                {btn.l}
              </button>
            ))}
          </div>
        </div>

        <div style={{fontSize:10, color:"#475569", marginBottom:12}}>{compounds.length} compounds shown</div>

        {/* COMPOUND LIST */}
        <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:24}}>
          {compounds.map((d,idx)=>{
            const spawn = isSpawn(d.name);
            const nums = d.bioavailNum || {};
            // `cat` is undefined on some records, which collided React keys and
            // made rows expand in pairs. Key on name, fall back to index.
            const rowId = ROW_ID.get(d) || `row-${idx}`;
            const advantage = (nums.liposomal||0) - (nums.oral||0);
            return (
              <div key={rowId} onClick={()=>setSelected(selected===rowId?null:rowId)}
                style={{padding:"12px 16px", background:spawn?"rgba(245,200,0,0.04)":"#0f0f0f", border:"1px solid", borderColor:selected===rowId?"#22d3ee":spawn?"rgba(245,200,0,0.25)":"#1a1a1a", borderLeft:`4px solid ${d.color||"#475569"}`, borderRadius:8, cursor:"pointer", transition:"all 0.15s"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8}}>
                  <div>
                    <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
                      <span style={{fontSize:13, fontWeight:700, color:spawn?"#f5c800":"#f1f5f9"}}>{d.name}</span>
                      {spawn && <span style={{fontSize:8, padding:"1px 6px", background:"rgba(245,200,0,0.15)", color:"#f5c800", border:"1px solid rgba(245,200,0,0.3)", borderRadius:10}}>★ SPAWN NUTRA</span>}
                      {advantage >= 40 && <span style={{fontSize:8, padding:"1px 6px", background:"rgba(34,211,238,0.1)", color:"#22d3ee", border:"1px solid rgba(34,211,238,0.3)", borderRadius:10}}>+{advantage}% LIPO ADVANTAGE</span>}
                    </div>
                    <div style={{fontSize:9, color:"#475569", marginTop:2}}>{d.category}</div>
                  </div>
                  <div style={{fontSize:9, color:"#dc2626", flexShrink:0, marginLeft:8}}>{selected===rowId?"▲":"▼"}</div>
                </div>
                <div style={{display:"flex", flexDirection:"column", gap:3}}>
                  {ROUTES.map(r=>(
                    <div key={r.key} style={{display:"flex", alignItems:"center", gap:8}}>
                      <div style={{width:72, fontSize:8, color:"#374151", flexShrink:0}}>{r.label.split(" ").slice(1).join(" ")}</div>
                      <div style={{flex:1, height:12, background:"#1a1a1a", borderRadius:2, overflow:"hidden", position:"relative"}}>
                        <div style={{
                          height:"100%", width:`${nums[r.key]||0}%`,
                          background: r.key==="liposomal" && spawn ? "linear-gradient(90deg,#f5c800,#dc2626)" : r.color,
                          borderRadius:2, transition:"width 0.4s ease",
                        }}>
                          {(nums[r.key]||0) >= 20 && <span style={{position:"absolute",right:3,top:"50%",transform:"translateY(-50%)",fontSize:7,color:"#fff",fontWeight:700}}>{nums[r.key]}%</span>}
                        </div>
                        {(nums[r.key]||0) > 0 && (nums[r.key]||0) < 20 && (
                          <span style={{position:"absolute",left:`${(nums[r.key]||0)+0.5}%`,top:"50%",transform:"translateY(-50%)",fontSize:7,color:"#475569"}}>{nums[r.key]}%</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selected===rowId && (
                  <div style={{marginTop:10, padding:"10px 14px", background:"#060608", borderRadius:6, borderTop:"1px solid #1a1a1a"}}>
                    <div style={{fontSize:11, color:"#94a3b8", lineHeight:1.8, marginBottom:8}}>{d.bioavail?.supplement || d.bioavail?.liposomal || d.desc || ""}</div>
                    {d.studies && d.studies.length > 0 && (
                      <div style={{fontSize:10, color:"#374151"}}>
                        {d.studies.slice(0,2).map((s,i)=>(
                          <div key={i} style={{marginTop:4}}>📚 {s.journal} {s.year}: {s.title?.slice(0,80)}...</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* LIPOSOMAL ADVANTAGE CALLOUT */}
        <div style={{padding:"20px", background:"linear-gradient(135deg,rgba(245,200,0,0.06),rgba(220,38,38,0.04))", border:"1px solid rgba(245,200,0,0.2)", borderRadius:10}}>
          <div style={{fontSize:10, color:"#f5c800", letterSpacing:"0.15em", marginBottom:14}}>TOP LIPOSOMAL ADVANTAGE — FROM BIBLE DATABASE</div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10, marginBottom:16}}>
            {topAdvantage.map((d,i)=>(
              <div key={i} style={{padding:"10px 14px", background:"#0f0f0f", borderRadius:6, border:"1px solid #1a1a1a"}}>
                <div style={{fontSize:10, color:isSpawn(d.name)?"#f5c800":"#94a3b8", marginBottom:6, fontWeight:700}}>{String(d.name || "").split(" ").slice(0,3).join(" ")}</div>
                <div style={{display:"flex", justifyContent:"space-between"}}>
                  <div style={{fontSize:10, color:"#475569"}}>Oral: <span style={{color:"#dc2626"}}>{d.bioavailNum.oral || 0}%</span></div>
                  <div style={{fontSize:10, color:"#f5c800"}}>+{d.advantage}%</div>
                  <div style={{fontSize:10, color:"#475569"}}>Lipo: <span style={{color:"#22d3ee"}}>{d.bioavailNum.liposomal || 0}%</span></div>
                </div>
                <div style={{marginTop:6, height:4, background:"#1a1a1a", borderRadius:2, overflow:"hidden"}}>
                  <div style={{height:"100%", width:`${d.bioavailNum.liposomal || 0}%`, background:"linear-gradient(90deg,#22d3ee,#22c55e)", borderRadius:2}}/>
                </div>
              </div>
            ))}
          </div>
          <div style={{fontSize:11, color:"#64748b", lineHeight:1.7}}>
            Spawn Nutra BioPro and Immortal use pharmaceutical-grade phospholipid bilayer nanovesicle technology. Compounds encapsulated in lipid vesicles 50–200nm bypass GI degradation and fuse directly with cell membranes — the same delivery mechanism cells use to communicate. Data sourced live from the {BIOAVAIL_COUNT}-compound Mad Scientist Bible database.
          </div>
        </div>
      </div>

      <div style={{padding:"12px 20px", borderTop:"1px solid #1a1a1a", textAlign:"center", fontSize:10, color:"#374151", letterSpacing:"0.08em"}}>
        MAD SCIENTIST 5150 · SPAWN NUTRA · DATA FROM COMPOUND BIBLE · NOT MEDICAL ADVICE
      </div>
      <style>{`* { box-sizing: border-box; } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #060608; } ::-webkit-scrollbar-thumb { background: #1a1a1a; border-radius: 2px; } select option { background: #0f0f0f; }`}</style>
    </div>
  );
}
